import Icon from '@mui/material/Icon'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Datetime from 'utils/datetime/datetime'
import styles from '../IntradayTradingView.module.less'
import type { IntradayPrices } from '../IntradayTradingView.types'

type IntradayPricesDisplayProps = {
  prices: IntradayPrices
  collapsible?: boolean
}

export function IntradayPricesDisplay({ prices, collapsible = true }: IntradayPricesDisplayProps): ReactNode {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!prices.times || !prices.values || prices.times.length === 0) {
    return null
  }

  const table = (
    <div className={styles.IntradayTradingView_PriceTableWrapper}>
      <table className={styles.IntradayTradingView_PriceTable}>
        <thead>
          <tr>
            <th className={styles.IntradayTradingView_PriceTableTh}>{t('Hour')}</th>
            <th className={styles.IntradayTradingView_PriceTableTh}>{t('Price (SEK/MWh)')}</th>
          </tr>
        </thead>
        <tbody>
          {prices.times.map((time, index) => (
            <tr key={index} className={styles.IntradayTradingView_PriceTableRow}>
              <td className={styles.IntradayTradingView_PriceTableLabelCell}>{formatHourLabel(time)}</td>
              <td className={styles.IntradayTradingView_PriceTableValueCell}>
                {prices.values[index]?.toFixed(2) ?? '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  if (!collapsible) {
    return <div className={styles.IntradayTradingView_PricesInfo}>{table}</div>
  }

  return (
    <div className={`${styles.IntradayTradingView_PricesInfo} ${styles.IntradayTradingView_OneThirdWidth}`}>
      <details
        open={isExpanded}
        className={styles.IntradayTradingView_PricesDetails}
        onToggle={(event) => setIsExpanded(event.currentTarget.open)}
      >
        <summary className={styles.IntradayTradingView_PricesDetailsTitle}>
          <span>{t('Used input prices')}</span>
          <Icon className={isExpanded ? 'fal fa-chevron-up' : 'fal fa-chevron-down'} fontSize="small" />
        </summary>
        {table}
      </details>
    </div>
  )
}

function formatHourLabel(time: ISODateTime | number): string {
  if (typeof time === 'number') {
    return String(time)
  }

  try {
    const startDate = Datetime.toLocalTime(time, 'onlyDate')
    const startHour = Datetime.toLocalTime(time, 'hour')

    // Calculate end date/time (may be next day if hour rolls over)
    const date = new Date(time)
    const endDate = new Date(date)
    endDate.setHours(endDate.getHours() + 1)
    const endIso = Datetime.toISOString(endDate)
    const endDateStr = Datetime.toLocalTime(endIso, 'onlyDate')
    const endHour = Datetime.toLocalTime(endIso, 'hour')

    // If dates are the same, show only once
    if (startDate === endDateStr) {
      return `${startDate} ${startHour}-${endHour}`
    }

    // If dates differ, show both
    return `${startDate} ${startHour} - ${endDateStr} ${endHour}`
  } catch {
    return String(time)
  }
}
